import React, { useState, useCallback, useEffect, useRef } from "react";
import { Plus, X } from "lucide-react";
import SectionCard, { SectionDraft } from "./SectionCard";
import { generateId } from "../../lib/cms/generateId";
import { SECTION_TYPES, SECTION_TYPE_MAP } from "../../lib/cms/sectionTypes";

interface SectionsBuilderProps {
  sections: SectionDraft[];
  onChange: (sections: SectionDraft[]) => void;
}

export default function SectionsBuilder({
  sections,
  onChange,
}: SectionsBuilderProps) {
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (showModal) return;
    openButtonRef.current?.focus();
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const title = modalRef.current?.querySelector<HTMLHeadingElement>(
      "#add-section-modal-title",
    );
    title?.focus();
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [showModal, closeModal]);

  const handleAdd = useCallback(
    (type: string) => {
      const info = SECTION_TYPE_MAP.get(type);
      if (!info) return;
      setShowModal(false);
      const newSection: SectionDraft = {
        _id: generateId(),
        type,
        ...info.createDefault(),
      };
      onChange([...sections, newSection]);
    },
    [sections, onChange],
  );

  const handleUpdate = useCallback(
    (id: string, data: Record<string, unknown>) => {
      if (!id) return;
      onChange(sections.map((s) => (s._id === id ? { ...s, ...data } : s)));
    },
    [sections, onChange],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (!id) return;
      onChange(sections.filter((s) => s._id !== id));
    },
    [sections, onChange],
  );

  const handleMoveUp = useCallback(
    (id: string) => {
      if (!id) return;
      const idx = sections.findIndex((s) => s._id === id);
      if (idx === -1 || idx === 0) return;
      const copy = [...sections];
      [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
      onChange(copy);
    },
    [sections, onChange],
  );

  const handleMoveDown = useCallback(
    (id: string) => {
      if (!id) return;
      const idx = sections.findIndex((s) => s._id === id);
      if (idx === -1 || idx >= sections.length - 1) return;
      const copy = [...sections];
      [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
      onChange(copy);
    },
    [sections, onChange],
  );

  return (
    <div className="space-y-3">
      {/* Hide background content from screen readers when modal is open */}
      <div aria-hidden={showModal || undefined}>
        {sections.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            Nenhuma secção definida. Clique no botão abaixo para adicionar.
          </p>
        )}

        <div className="space-y-2">
          {sections.map((section, index) => (
            <SectionCard
              key={section._id}
              section={section}
              index={index}
              total={sections.length}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          ))}
        </div>
      </div>

      <button
        ref={openButtonRef}
        type="button"
        onClick={() => setShowModal(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 transition"
      >
        <Plus size={16} /> Adicionar Secção
      </button>

      {/* Add Section Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-section-modal-title"
          onClick={closeModal}
        >
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2
                id="add-section-modal-title"
                className="text-lg font-medium text-gray-800"
                tabIndex={-1}
              >
                Adicionar Secção
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600"
                aria-label="Fechar"
                title="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SECTION_TYPES.map((info) => (
                <button
                  key={info.type}
                  type="button"
                  onClick={() => handleAdd(info.type)}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition text-center"
                >
                  <span className="text-gray-600" aria-hidden="true">
                    <info.Icon size={20} />
                  </span>
                  <span className="font-medium text-sm text-gray-800">
                    {info.label}
                  </span>
                  <span className="text-xs text-gray-500 leading-tight">
                    {info.description}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
